import { create } from 'zustand';
import { api } from '../services/api';

// Canvas Settings
export interface CanvasSettings {
  width: number;
  height: number;
  background_type: 'color' | 'gradient' | 'image';
  background_value: string;
}

// Template Element
export interface TemplateElement {
  id: string;
  type: 'text' | 'image' | 'shape' | 'icon' | 'group';
  position: { x: number; y: number };
  size: { width: number; height: number };
  style: Record<string, unknown>;
  content?: string;
  variable_binding?: string;
  lockAspectRatio?: boolean; // Seitenverhältnis beim Resizen beibehalten
}

// Template Variable
export interface TemplateVariable {
  id: string;
  name: string;
  type: 'text' | 'image' | 'color' | 'number';
  placeholder?: string;
  default_value?: string;
  ai_prompt_hint?: string;
}

// Design Template
export interface DesignTemplate {
  id: string;
  name: string;
  description?: string;
  thumbnail_url?: string;
  canvas: CanvasSettings;
  elements: TemplateElement[];
  variables: TemplateVariable[];
  category: 'carousel' | 'story' | 'post' | 'quote';
  is_public: boolean;
  is_system: boolean;
  created_by?: string;
  tenant_id?: string;
  created_at: string;
  updated_at: string;
}

// Slide Content
export interface SlideContent {
  slide_number: number;
  variable_values: Record<string, string>;
}

// Carousel Project
export interface CarouselProject {
  id: string;
  display_id: string;
  project_id: string;
  template_id?: string;
  name: string;
  slides: SlideContent[];
  custom_canvas?: CanvasSettings;
  custom_elements?: TemplateElement[];
  status: 'draft' | 'generated' | 'posted';
  generated_urls: string[];
  post_id?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// Design Status
export type DesignStatus = 'draft' | 'in_progress' | 'completed';

// Saved Design (gespeicherte Creator-Formate)
export interface SavedDesign {
  id: string;
  display_id: string;
  project_id: string;
  name: string;
  description?: string;
  thumbnail_url?: string;
  canvas: CanvasSettings;
  elements: TemplateElement[];
  audio_tracks: TemplateElement[];
  category: 'post' | 'story' | 'reel' | 'carousel';
  platform: 'facebook' | 'instagram' | 'both';
  tags: string[];
  status: DesignStatus;
  flight_id?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// Video Export Result
export interface VideoExportResult {
  url: string;
  width: number;
  height: number;
  duration: number;
  filename: string;
}

// LLM Template (für Post-Serien-Generierung)
export interface LLMTemplateContentSheet {
  description?: string;
  fields?: Array<{
    field_name: string;
    element_id?: string;
    type: string;
    required?: boolean;
    semantic_hint?: string;
    current_value?: string;
    constraints?: Record<string, unknown>;
    description?: string;
    example?: string;
  }>;
  csv_header?: string;
  example_row?: string;
}

export interface LLMTemplateInstructions {
  purpose?: string;
  single_source_of_truth?: string;
  batch_workflow?: string[];
  constraint_semantics?: Record<string, string>;
  content_guidelines?: Record<string, string[]>;
  output_format?: {
    description?: string;
    json_array?: boolean;
    example?: Array<Record<string, string>>;
  };
}

export interface LLMTemplate {
  id: string;
  display_id: string;
  project_id: string;
  user_id: string;
  name: string;
  description?: string;
  template_type: 'campaign' | 'editorial' | 'series' | 'evergreen' | 'seasonal';
  category: 'post' | 'story' | 'reel' | 'carousel';
  canvas: CanvasSettings;
  elements: TemplateElement[];
  brand_config: Record<string, unknown>;
  series_config: {
    content_sheet?: LLMTemplateContentSheet;
    llm_instructions?: LLMTemplateInstructions;
    series_options?: Record<string, unknown>;
  };
  schedule_config: Record<string, unknown>;
  platform_config: { default: string[] };
  times_used: number;
  last_used_at?: string;
  status: 'draft' | 'active' | 'archived';
  created_at: string;
  updated_at: string;
}

// LLM Template Create/Update Request
export interface LLMTemplateRequest {
  project_id: string;
  name: string;
  description?: string;
  template_type?: 'campaign' | 'editorial' | 'series' | 'evergreen' | 'seasonal';
  category?: 'post' | 'story' | 'reel' | 'carousel';
  canvas: CanvasSettings;
  elements: TemplateElement[];
  brand_config?: Record<string, unknown>;
  series_config?: Record<string, unknown>;
  schedule_config?: Record<string, unknown>;
  platform_config?: { default: string[] };
  status?: 'draft' | 'active' | 'archived';
}

// Video Export Request
export interface VideoExportRequest {
  project_id: string;
  canvas: CanvasSettings;
  elements: TemplateElement[];
  audio_tracks?: {
    url: string;
    startTime?: number;
    duration?: number;
    volume?: number;
    fadeInDuration?: number;  // Fade-in based on VIDEO duration
    fadeOutDuration?: number; // Fade-out based on VIDEO duration
    loop?: boolean;
  }[];
  duration?: number;
  fps?: number;
}

// Preview Request - two modes:
// 1. Template-based: template_id + slide_content
// 2. Custom: canvas + elements + variables
export interface PreviewRequest {
  // Template-based mode
  template_id?: string;
  slide_content?: SlideContent;
  // Custom mode
  canvas?: CanvasSettings;
  elements?: TemplateElement[];
  variables?: Record<string, string>;
}

interface CreatorState {
  // Templates
  templates: DesignTemplate[];
  selectedTemplate: DesignTemplate | null;

  // Carousel Projects
  carouselProjects: CarouselProject[];
  currentProject: CarouselProject | null;

  // Saved Designs
  savedDesigns: SavedDesign[];
  selectedDesign: SavedDesign | null;

  // LLM Templates
  llmTemplates: LLMTemplate[];
  selectedLLMTemplate: LLMTemplate | null;
  editingLLMTemplateId: string | null;

  // Editor State
  slides: SlideContent[];
  currentSlideIndex: number;
  previewUrl: string | null;

  // Loading States
  isLoading: boolean;
  isGenerating: boolean;
  isSaving: boolean;
  isExportingVideo: boolean;
  error: string | null;

  // Video Export
  ffmpegAvailable: boolean | null;
  lastVideoExport: VideoExportResult | null;

  // Actions - Templates
  fetchTemplates: (category?: string) => Promise<void>;
  selectTemplate: (template: DesignTemplate | null) => void;
  createTemplate: (data: Partial<DesignTemplate>) => Promise<DesignTemplate>;
  updateTemplate: (id: string, data: Partial<DesignTemplate>) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;

  // Actions - Carousel Projects
  fetchCarouselProjects: (projectId: string) => Promise<void>;
  createCarouselProject: (data: { project_id: string; template_id?: string; name: string; slides?: SlideContent[] }) => Promise<CarouselProject>;
  updateCarouselProject: (id: string, data: Partial<CarouselProject>) => Promise<void>;
  deleteCarouselProject: (id: string) => Promise<void>;
  generateCarouselImages: (projectId: string) => Promise<string[]>;

  // Actions - Saved Designs
  fetchSavedDesigns: (projectId: string, category?: string) => Promise<void>;
  getSavedDesign: (id: string) => Promise<SavedDesign>;
  saveDesign: (data: {
    project_id: string;
    name: string;
    description?: string;
    thumbnail_url?: string;
    canvas: CanvasSettings;
    elements: TemplateElement[];
    category?: 'post' | 'story' | 'reel' | 'carousel';
    platform?: 'facebook' | 'instagram' | 'both';
    tags?: string[];
    flight_id?: string;
  }) => Promise<SavedDesign>;
  updateDesign: (id: string, data: Partial<SavedDesign>) => Promise<void>;
  deleteDesign: (id: string) => Promise<void>;
  duplicateDesign: (id: string) => Promise<SavedDesign>;
  loadDesign: (design: SavedDesign) => void;

  // Actions - Editor
  setSlides: (slides: SlideContent[]) => void;
  updateSlide: (index: number, content: Partial<SlideContent>) => void;
  addSlide: () => void;
  removeSlide: (index: number) => void;
  setCurrentSlideIndex: (index: number) => void;
  generatePreview: (request: PreviewRequest) => Promise<string>;

  // Actions - Video Export
  checkFfmpegAvailable: () => Promise<boolean>;
  exportVideo: (request: VideoExportRequest) => Promise<VideoExportResult>;
  exportDesignAsVideo: (designId: string, options?: { duration?: number; fps?: number }) => Promise<VideoExportResult>;

  // Actions - LLM Templates
  fetchLLMTemplates: (projectId: string) => Promise<void>;
  getLLMTemplate: (id: string) => Promise<LLMTemplate>;
  createLLMTemplate: (data: LLMTemplateRequest) => Promise<LLMTemplate>;
  updateLLMTemplate: (id: string, data: Partial<LLMTemplateRequest>) => Promise<LLMTemplate>;
  deleteLLMTemplate: (id: string) => Promise<void>;
  importLLMTemplate: (projectId: string, templateJson: Record<string, unknown>) => Promise<LLMTemplate>;
  renderBatchFromTemplate: (templateId: string, projectId: string, items: Array<{ content_id?: string; content: Record<string, string> }>, saveToMedia?: boolean) => Promise<Array<{ content_id: string; url?: string; image?: string; width: number; height: number; media_id?: string }>>;
  createDesignsFromTemplate: (templateId: string, projectId: string, items: Array<{ content_id?: string; content: Record<string, string> }>) => Promise<{ designs: SavedDesign[]; count: number }>;
  setEditingLLMTemplateId: (id: string | null) => void;
  generateLLMPrompt: (id: string, options?: { posts_count?: number; start_date?: string }) => Promise<string>;

  // Utils
  clearError: () => void;
  reset: () => void;
}

export const useCreatorStore = create<CreatorState>((set, get) => ({
  // Initial State
  templates: [],
  selectedTemplate: null,
  carouselProjects: [],
  currentProject: null,
  savedDesigns: [],
  selectedDesign: null,
  llmTemplates: [],
  selectedLLMTemplate: null,
  editingLLMTemplateId: null,
  slides: [{ slide_number: 1, variable_values: {} }],
  currentSlideIndex: 0,
  previewUrl: null,
  isLoading: false,
  isGenerating: false,
  isSaving: false,
  isExportingVideo: false,
  error: null,
  ffmpegAvailable: null,
  lastVideoExport: null,

  // Template Actions
  fetchTemplates: async (category?: string) => {
    set({ isLoading: true, error: null });
    try {
      const params = category ? `?category=${category}` : '';
      const response = await api.get<{ data: DesignTemplate[] }>(`/creator/templates${params}`);
      set({ templates: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Templates';
      set({ error: message, isLoading: false });
    }
  },

  selectTemplate: (template: DesignTemplate | null) => {
    set({ selectedTemplate: template });
    if (template) {
      // Initialize slides with default values from template variables
      const defaultValues: Record<string, string> = {};
      template.variables.forEach(v => {
        if (v.default_value) {
          defaultValues[v.id] = v.default_value;
        }
      });
      set({
        slides: [{ slide_number: 1, variable_values: defaultValues }],
        currentSlideIndex: 0,
        previewUrl: null,
      });
    }
  },

  createTemplate: async (data: Partial<DesignTemplate>) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: DesignTemplate }>('/creator/templates', data);
      const newTemplate = response.data;
      set(state => ({
        templates: [newTemplate, ...state.templates],
        isLoading: false,
      }));
      return newTemplate;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Erstellen des Templates';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateTemplate: async (id: string, data: Partial<DesignTemplate>) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.put<{ data: DesignTemplate }>(`/creator/templates/${id}`, data);
      const updated = response.data;
      set(state => ({
        templates: state.templates.map(t => t.id === id ? updated : t),
        selectedTemplate: state.selectedTemplate?.id === id ? updated : state.selectedTemplate,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren des Templates';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deleteTemplate: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/creator/templates/${id}`);
      set(state => ({
        templates: state.templates.filter(t => t.id !== id),
        selectedTemplate: state.selectedTemplate?.id === id ? null : state.selectedTemplate,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Löschen des Templates';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  // Carousel Project Actions
  fetchCarouselProjects: async (projectId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: CarouselProject[] }>(`/creator/projects?project_id=${projectId}`);
      set({ carouselProjects: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Carousel-Projekte';
      set({ error: message, isLoading: false });
    }
  },

  createCarouselProject: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: CarouselProject }>('/creator/projects', data);
      const newProject = response.data;
      set(state => ({
        carouselProjects: [newProject, ...state.carouselProjects],
        currentProject: newProject,
        isLoading: false,
      }));
      return newProject;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Erstellen des Projekts';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateCarouselProject: async (id: string, data: Partial<CarouselProject>) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.put<{ data: CarouselProject }>(`/creator/projects/${id}`, data);
      const updated = response.data;
      set(state => ({
        carouselProjects: state.carouselProjects.map(p => p.id === id ? updated : p),
        currentProject: state.currentProject?.id === id ? updated : state.currentProject,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren des Projekts';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deleteCarouselProject: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/creator/projects/${id}`);
      set(state => ({
        carouselProjects: state.carouselProjects.filter(p => p.id !== id),
        currentProject: state.currentProject?.id === id ? null : state.currentProject,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Löschen des Projekts';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  generateCarouselImages: async (projectId: string) => {
    set({ isGenerating: true, error: null });
    try {
      const response = await api.post<{ data: { urls: string[] } }>(`/creator/projects/${projectId}/generate`, {});
      const urls = response.data.urls;
      set(state => ({
        carouselProjects: state.carouselProjects.map(p =>
          p.id === projectId ? { ...p, generated_urls: urls, status: 'generated' as const } : p
        ),
        currentProject: state.currentProject?.id === projectId
          ? { ...state.currentProject, generated_urls: urls, status: 'generated' as const }
          : state.currentProject,
        isGenerating: false,
      }));
      return urls;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Generieren der Bilder';
      set({ error: message, isGenerating: false });
      throw error;
    }
  },

  // Editor Actions
  setSlides: (slides: SlideContent[]) => set({ slides }),

  updateSlide: (index: number, content: Partial<SlideContent>) => {
    set(state => ({
      slides: state.slides.map((slide, i) =>
        i === index ? { ...slide, ...content } : slide
      ),
    }));
  },

  addSlide: () => {
    const { slides, selectedTemplate } = get();
    const defaultValues: Record<string, string> = {};
    selectedTemplate?.variables.forEach(v => {
      if (v.default_value) {
        defaultValues[v.id] = v.default_value;
      }
    });
    set({
      slides: [...slides, { slide_number: slides.length + 1, variable_values: defaultValues }],
      currentSlideIndex: slides.length,
    });
  },

  removeSlide: (index: number) => {
    const { slides, currentSlideIndex } = get();
    if (slides.length <= 1) return;

    const newSlides = slides
      .filter((_, i) => i !== index)
      .map((slide, i) => ({ ...slide, slide_number: i + 1 }));

    set({
      slides: newSlides,
      currentSlideIndex: Math.min(currentSlideIndex, newSlides.length - 1),
    });
  },

  setCurrentSlideIndex: (index: number) => set({ currentSlideIndex: index }),

  generatePreview: async (request: PreviewRequest) => {
    set({ isGenerating: true, error: null });
    try {
      const response = await api.post<{ data: { preview: string } }>('/creator/preview', request);
      const previewUrl = response.data.preview;
      set({ previewUrl, isGenerating: false });
      return previewUrl;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Generieren der Vorschau';
      set({ error: message, isGenerating: false });
      throw error;
    }
  },

  // Saved Designs Actions
  fetchSavedDesigns: async (projectId: string, category?: string) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams({ project_id: projectId });
      if (category) params.append('category', category);
      const response = await api.get<{ data: SavedDesign[] }>(`/creator/designs?${params}`);
      set({ savedDesigns: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Designs';
      set({ error: message, isLoading: false });
    }
  },

  getSavedDesign: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: SavedDesign }>(`/creator/designs/${id}`);
      set({ selectedDesign: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden des Designs';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  saveDesign: async (data) => {
    set({ isSaving: true, error: null });
    try {
      const response = await api.post<{ data: SavedDesign }>('/creator/designs', data);
      const newDesign = response.data;
      set(state => ({
        savedDesigns: [newDesign, ...state.savedDesigns],
        isSaving: false,
      }));
      return newDesign;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Speichern des Designs';
      set({ error: message, isSaving: false });
      throw error;
    }
  },

  updateDesign: async (id: string, data: Partial<SavedDesign>) => {
    set({ isSaving: true, error: null });
    try {
      const response = await api.put<{ data: SavedDesign }>(`/creator/designs/${id}`, data);
      const updated = response.data;
      set(state => ({
        savedDesigns: state.savedDesigns.map(d => d.id === id ? updated : d),
        selectedDesign: state.selectedDesign?.id === id ? updated : state.selectedDesign,
        isSaving: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren des Designs';
      set({ error: message, isSaving: false });
      throw error;
    }
  },

  deleteDesign: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/creator/designs/${id}`);
      set(state => ({
        savedDesigns: state.savedDesigns.filter(d => d.id !== id),
        selectedDesign: state.selectedDesign?.id === id ? null : state.selectedDesign,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Löschen des Designs';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  duplicateDesign: async (id: string) => {
    set({ isSaving: true, error: null });
    try {
      const response = await api.post<{ data: SavedDesign }>(`/creator/designs/${id}/duplicate`, {});
      const duplicate = response.data;
      set(state => ({
        savedDesigns: [duplicate, ...state.savedDesigns],
        isSaving: false,
      }));
      return duplicate;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Duplizieren des Designs';
      set({ error: message, isSaving: false });
      throw error;
    }
  },

  loadDesign: (design: SavedDesign) => {
    set({
      selectedDesign: design,
      previewUrl: null,
    });
  },

  // Video Export Actions
  checkFfmpegAvailable: async () => {
    try {
      const response = await api.get<{ data: { ffmpeg_available: boolean } }>('/creator/video/check');
      const available = response.data.ffmpeg_available;
      set({ ffmpegAvailable: available });
      return available;
    } catch (error) {
      console.error('Error checking ffmpeg availability:', error);
      set({ ffmpegAvailable: false });
      return false;
    }
  },

  exportVideo: async (request: VideoExportRequest) => {
    set({ isExportingVideo: true, error: null });
    try {
      const response = await api.post<{ data: VideoExportResult }>('/creator/export-video', request);
      const result = response.data;
      set({ lastVideoExport: result, isExportingVideo: false });
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Video-Export';
      set({ error: message, isExportingVideo: false });
      throw error;
    }
  },

  exportDesignAsVideo: async (designId: string, options?: { duration?: number; fps?: number }) => {
    set({ isExportingVideo: true, error: null });
    try {
      const response = await api.post<{ data: VideoExportResult }>(
        `/creator/designs/${designId}/export-video`,
        options || {}
      );
      const result = response.data;
      set({ lastVideoExport: result, isExportingVideo: false });
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Video-Export';
      set({ error: message, isExportingVideo: false });
      throw error;
    }
  },

  // LLM Template Actions
  fetchLLMTemplates: async (projectId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: LLMTemplate[] }>(`/creator/llm-templates?project_id=${projectId}`);
      set({ llmTemplates: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der LLM-Templates';
      set({ error: message, isLoading: false });
    }
  },

  getLLMTemplate: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: LLMTemplate }>(`/creator/llm-templates/${id}`);
      set({ selectedLLMTemplate: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden des LLM-Templates';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  createLLMTemplate: async (data: LLMTemplateRequest) => {
    set({ isSaving: true, error: null });
    try {
      const response = await api.post<{ data: LLMTemplate }>('/creator/llm-templates', data);
      const newTemplate = response.data;
      set(state => ({
        llmTemplates: [newTemplate, ...state.llmTemplates],
        selectedLLMTemplate: newTemplate,
        editingLLMTemplateId: newTemplate.id,
        isSaving: false,
      }));
      return newTemplate;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Erstellen des LLM-Templates';
      set({ error: message, isSaving: false });
      throw error;
    }
  },

  updateLLMTemplate: async (id: string, data: Partial<LLMTemplateRequest>) => {
    set({ isSaving: true, error: null });
    try {
      const response = await api.put<{ data: LLMTemplate }>(`/creator/llm-templates/${id}`, data);
      const updated = response.data;
      set(state => ({
        llmTemplates: state.llmTemplates.map(t => t.id === id ? updated : t),
        selectedLLMTemplate: state.selectedLLMTemplate?.id === id ? updated : state.selectedLLMTemplate,
        isSaving: false,
      }));
      return updated;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren des LLM-Templates';
      set({ error: message, isSaving: false });
      throw error;
    }
  },

  deleteLLMTemplate: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/creator/llm-templates/${id}`);
      set(state => ({
        llmTemplates: state.llmTemplates.filter(t => t.id !== id),
        selectedLLMTemplate: state.selectedLLMTemplate?.id === id ? null : state.selectedLLMTemplate,
        editingLLMTemplateId: state.editingLLMTemplateId === id ? null : state.editingLLMTemplateId,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Löschen des LLM-Templates';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  importLLMTemplate: async (projectId: string, templateJson: Record<string, unknown>) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: LLMTemplate }>('/creator/llm-templates/import', {
        project_id: projectId,
        template_json: templateJson,
      });
      const newTemplate = response.data;
      set(state => ({
        llmTemplates: [...state.llmTemplates, newTemplate],
        isLoading: false,
      }));
      return newTemplate;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Importieren des Templates';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setEditingLLMTemplateId: (id: string | null) => {
    set({ editingLLMTemplateId: id });
  },

  renderBatchFromTemplate: async (
    templateId: string,
    projectId: string,
    items: Array<{ content_id?: string; content: Record<string, string> }>,
    saveToMedia = true
  ) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{
        data: {
          rendered: Array<{
            content_id: string;
            url?: string;
            image?: string;
            width: number;
            height: number;
            media_id?: string;
          }>;
        };
      }>(`/creator/llm-templates/${templateId}/render-batch`, {
        items,
        save_to_media: saveToMedia,
        project_id: projectId,
      });
      set({ isLoading: false });
      return response.data.rendered;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Batch-Rendern';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  createDesignsFromTemplate: async (
    templateId: string,
    projectId: string,
    items: Array<{ content_id?: string; content: Record<string, string> }>
  ) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{
        data: {
          designs: SavedDesign[];
          count: number;
        };
      }>(`/creator/llm-templates/${templateId}/create-designs`, {
        items,
        project_id: projectId,
      });
      // Add new designs to saved designs list
      set(state => ({
        savedDesigns: [...response.data.designs, ...state.savedDesigns],
        isLoading: false,
      }));
      return response.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Erstellen der Designs';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  generateLLMPrompt: async (id: string, options?: { posts_count?: number; start_date?: string }) => {
    set({ isGenerating: true, error: null });
    try {
      const response = await api.post<{ data: { prompt: string } }>(
        `/creator/llm-templates/${id}/generate-prompt`,
        options || {}
      );
      set({ isGenerating: false });
      return response.data.prompt;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Generieren des LLM-Prompts';
      set({ error: message, isGenerating: false });
      throw error;
    }
  },

  // Utils
  clearError: () => set({ error: null }),

  reset: () => set({
    selectedTemplate: null,
    currentProject: null,
    selectedDesign: null,
    selectedLLMTemplate: null,
    editingLLMTemplateId: null,
    slides: [{ slide_number: 1, variable_values: {} }],
    currentSlideIndex: 0,
    previewUrl: null,
    error: null,
    lastVideoExport: null,
  }),
}));
